import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Chip, Flex, Heading, Pagination, Spinner, Text, TextField } from "@telia/teddy";
import { API_URL_SUPERADMIN_FEEDBACK } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import UserFeedbackCard from "../Feedbacks/UserFeedbackCard";
import { type FeedbackEntry } from "./feedbackTypes";
import RecentActivity from "./RecentActivity";
import "../Feedbacks/Feedbacks.less";

const PAGE_SIZE = 20;

type FilterMode = "all" | "unresolved" | "resolved" | "activity";

const FILTER_VALUES = new Set<string>(["all", "unresolved", "resolved", "activity"]);
const isFilterMode = (value: string): value is FilterMode => FILTER_VALUES.has(value);

const isUnresolved = (entry: FeedbackEntry) => entry.status === "open";
const isResolved = (entry: FeedbackEntry) => entry.status !== "open";

const FEEDBACK_TYPES = ["Bug", "Generell tilbakemelding", "Forslag til forbedring"] as const;

const SuperAdminFeedbackList: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const filterParam = searchParams.get("filter") ?? "";
    const filter: FilterMode = isFilterMode(filterParam) ? filterParam : "unresolved";
    const search = searchParams.get("search") ?? "";
    const pageParam = Number(searchParams.get("page"));
    const page = pageParam >= 1 ? pageParam : 1;

    const updateParams = useCallback(
        (updates: Record<string, string | null>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    for (const [key, value] of Object.entries(updates)) {
                        if (value) {
                            next.set(key, value);
                        } else {
                            next.delete(key);
                        }
                    }
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    useEffect(() => {
        const fetchFeedback = async () => {
            setLoading(true);
            setError(null);
            try {
                const results = await Promise.all(
                    FEEDBACK_TYPES.map(async (type) => {
                        const res = await retailerFetch(
                            `${API_URL_SUPERADMIN_FEEDBACK}?feedbackType=${encodeURIComponent(type)}`,
                        );
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.json() as Promise<FeedbackEntry[]>;
                    }),
                );
                const all = results.flat().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
                setFeedback(all);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Kunne ikke hente tilbakemeldinger");
            } finally {
                setLoading(false);
            }
        };
        fetchFeedback();
    }, []);

    const searchMatches = useMemo(() => {
        if (!search.trim()) return feedback;
        const q = search.trim().toLowerCase();
        return feedback.filter(
            (e) =>
                e.description?.toLowerCase().includes(q) ||
                e.agentName?.toLowerCase().includes(q) ||
                e.dealerName?.toLowerCase().includes(q),
        );
    }, [feedback, search]);

    const allCount = searchMatches.length;
    const unresolvedCount = useMemo(() => searchMatches.filter(isUnresolved).length, [searchMatches]);
    const resolvedCount = useMemo(() => searchMatches.filter(isResolved).length, [searchMatches]);

    const filtered = useMemo(() => {
        if (filter === "unresolved") {
            return searchMatches.filter(isUnresolved);
        }
        if (filter === "resolved") {
            return searchMatches.filter(isResolved);
        }
        return searchMatches;
    }, [searchMatches, filter]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handlePageChange = (newPage: number) => {
        updateParams({ page: newPage > 1 ? String(newPage) : null });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleFilterChange = (val: string) => {
        if (val) {
            updateParams({ filter: val === "unresolved" ? null : val, page: null });
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateParams({ search: e.target.value || null, page: null });
    };

    return (
        <Box className="feedbacks-page">
            <Heading mb="100" as="h1" variant="title-300" className="feedbacks-page__header">
                Tilbakemeldinger
            </Heading>

            {loading && (
                <Box className="feedbacks-page__loading">
                    <Spinner size="md" />
                </Box>
            )}

            {error && (
                <Box className="feedbacks-page__error">
                    <Text>{error}</Text>
                </Box>
            )}

            {!loading && !error && (
                <>
                    <Flex gap="200" mb="300" direction="column">
                        <Chip type="single" value={filter} onValueChange={handleFilterChange} variant="choice">
                            <Chip.Item value="all">Alle ({allCount})</Chip.Item>
                            <Chip.Item value="unresolved">Kun uløste ({unresolvedCount})</Chip.Item>{" "}
                            <Chip.Item value="resolved">Kun løste ({resolvedCount})</Chip.Item>{" "}
                            <Chip.Item value="activity">Nylig aktivitet</Chip.Item>
                        </Chip>
                        {filter !== "activity" && (
                            <TextField>
                                <TextField.Input
                                    placeholder="Søk i beskrivelse, navn eller butikk..."
                                    value={search}
                                    onChange={handleSearchChange}
                                />
                            </TextField>
                        )}
                    </Flex>

                    {filter === "activity" ? (
                        <RecentActivity />
                    ) : filtered.length === 0 ? (
                        <Box className="feedbacks-page__empty">
                            <Text>Ingen tilbakemeldinger funnet.</Text>
                        </Box>
                    ) : (
                        <>
                            <Flex direction="column" gap="200">
                                {pageItems.map((entry) => (
                                    <UserFeedbackCard key={entry.id} entry={entry} shouldShowName={true} />
                                ))}
                            </Flex>

                            {totalPages > 1 && (
                                <Pagination aria-label="Sidenavigasjon" style={{ marginTop: "24px" }}>
                                    <Pagination.Navigation
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        side="prev"
                                        showLabels={false}
                                    />
                                    <Pagination.PageNumbers
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        maxVisiblePages={5}
                                    />
                                    <Pagination.Navigation
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        side="next"
                                        showLabels={false}
                                    />
                                </Pagination>
                            )}
                        </>
                    )}
                </>
            )}
        </Box>
    );
};

export default SuperAdminFeedbackList;
